import { useCallback, useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { useTheme } from 'styled-components';
import { HeroImage } from '@geberit/gdds';

// styles
import { StyledHeroImageThemedWrapper, Title } from './hero-image-themed.styles';
import styles from './hero-image-themed.module.scss';

// types
import type { HeroImageThemedProps } from './hero-image-themed.types';
import type { ThemeType } from '@geberit/gdds';

// components
import { HeroImageThemedDefault } from './hero-image-themed-default';

// utils
import { useThemeName } from 'utils/hooks/use-theme';
import { classNameBuilder } from 'utils/classNameBuilder';
import { getIdsOfEmptyContentBoxes } from './hero-image-themed.utils';
import { useContent } from 'components/Page/page-hooks';
import { useXy } from 'utils/hooks/use-xy';
import useOnInitialHeaderTransitionsEnded from './useOnInitialHeaderTransitionsEnded';
import { useIsDesktop } from 'components/App/SizeProvider';
import { InnerHtml } from 'components/InnerHtml/inner-html';

const DELAY_DEFAULT = 6;
const DELAY_INFINITY = 99999;

function HeroImageThemed(props: HeroImageThemedProps) {
  const {
    contentBoxPosition,
    isSeoConformCarousel,
    items,
    isSection,
    hideScrollIndicator,
    disableAnimation,
    disableSliderControls,
    wideLayout,
    previewId,
    autoplay = true,
    className,
    titleTag,
    onCarouselChange,
  } = props;
  const { ref: refSlider, inView } = useInView({
    threshold: 0,
  });

  const { page } = useContent();
  const theme = useTheme() as ThemeType;
  const themeName = useThemeName();
  const isXY = useXy();
  const isDesktop = useIsDesktop();

  function calculateNavHeight() {
    if (isDesktop) {
      const pageHeaderHeight = document.getElementById('page-header')?.offsetHeight;
      const megaMenuHeight = document.getElementById('first-level-navigation')?.offsetHeight;
      setNavHeight((pageHeaderHeight ?? 0) + (megaMenuHeight ?? 0));
    } else {
      if (isXY) {
        setNavHeight(
          (document.querySelector('#page-header nav') as HTMLElement)?.offsetHeight ?? 0,
        );
      } else {
        setNavHeight(
          (document.querySelector('#page-header nav > div') as HTMLElement)?.offsetHeight ?? 0,
        );
      }
    }
  }

  useEffect(() => {
    calculateNavHeight();
  }, [isDesktop]);

  const [navHeight, setNavHeight] = useState<number>(0);

  const [isForgroundVideoOpen, setIsForgroundVideoOpen] = useState<boolean>(false);

  const emptyContentBoxIds = getIdsOfEmptyContentBoxes({ items });

  const onTransitionEndCallback = useCallback(() => {
    calculateNavHeight();
  }, []);

  useOnInitialHeaderTransitionsEnded(
    typeof document !== 'undefined' ? document.getElementById('page-header') : null,
    onTransitionEndCallback,
  );

  const isNordicsContentPage = page?.type === 'nordics_contentpage';

  useEffect(() => {
    if (isForgroundVideoOpen) return;
    window?.swiper?.autoplay?.run();
  }, [isForgroundVideoOpen]);

  const renderTitle = (item) => {
    const { emphasize, title, subtitle } = item;

    if (title?.length === 0 && subtitle?.length === 0) {
      return null;
    }

    return (
      <Title
        as={titleTag ?? 'span'}
        fontWeight={emphasize === 'title' ? theme.fontWeights.bold : theme.fontWeights.light}
        className={classNameBuilder(
          'gdds-headline',
          styles.heroImageThemedHTag,
          titleTag ? styles.DefaultFont : styles.HeroFont,
        )}
      >
        <InnerHtml as="span" content={title} />
        <Title
          fontWeight={emphasize === 'subtitle' ? theme.fontWeights.bold : theme.fontWeights.light}
        >
          {subtitle?.slice(0, 50)}
        </Title>
      </Title>
    );
  };

  if (themeName === 'DEFAULT') {
    return (
      <div ref={refSlider}>
        <HeroImageThemedDefault
          {...props}
          emptyContentBoxIds={emptyContentBoxIds}
          isNordicsContentPage={isNordicsContentPage}
          navHeight={navHeight}
          isForgroundVideoOpen={isForgroundVideoOpen}
          setIsForgroundVideoOpen={setIsForgroundVideoOpen}
          sliderDelay={isForgroundVideoOpen || !inView ? DELAY_INFINITY : DELAY_DEFAULT}
          isSection={isSection}
        />
      </div>
    );
  }

  return (
    <StyledHeroImageThemedWrapper
      className={classNameBuilder(
        'hero-image-themed',
        hideScrollIndicator && 'hide-scrollbar-indicator',
        disableAnimation && 'disable-animation',
        disableSliderControls && 'disable-slider-controls',
        wideLayout && 'wide-layout',
        className,
      )}
      emptyContentBoxIds={emptyContentBoxIds}
      isSlider={items.length > 1}
      ref={refSlider}
    >
      <HeroImage
        type={items.length > 1 ? 'b' : 'a'}
        items={items}
        previewId={previewId ? previewId : isNordicsContentPage ? '#pt_heroTypeABItems' : undefined}
        previewIdExtractor={
          previewId || isNordicsContentPage
            ? (_, index) => {
                return `#${index}`;
              }
            : undefined
        }
        navigationHeight={navHeight}
        isSeoConformCarousel={isSeoConformCarousel}
        contentBoxPosition={contentBoxPosition}
        slidingInterval={autoplay ? undefined : DELAY_INFINITY}
        onCarouselChange={onCarouselChange}
        renderTitle={renderTitle}
      />
    </StyledHeroImageThemedWrapper>
  );
}

export { HeroImageThemed };
